import React from 'react';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { SwapOutlined } from '@ant-design/icons';
import { usePermissions } from '@shared/permissions/usePermissions';
import { userSettingsAtom, USER_SETTINGS_DEFAULTS } from '@shared/state/siteState';
import { useTestDataRemote } from '@lib/util/site';
import {
  getAlternateEnvironmentLabel,
  getAlternateEnvironmentUrl,
} from '@lib/util/environmentUrls';
import theme from '@config/theme';
import { rgba } from '@config/palette/utils';

const HopButton = styled.button<{ $staging: boolean }>`
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 99990;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${theme.palette.neutral.white};
  background-color: ${({ $staging }) => ($staging ? '#FF0000' : theme.palette.primary.hue)};
  box-shadow:
    0 4px 14px ${rgba('#000000', 0.18)},
    0 1px 3px ${rgba('#000000', 0.12)};
  transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    background-color: ${({ $staging }) => ($staging ? '#c72d22' : theme.palette.primary.D100)};
    box-shadow:
      0 8px 18px ${rgba('#000000', 0.22)},
      0 2px 4px ${rgba('#000000', 0.14)};
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ $staging }) => ($staging ? '#FF0000' : theme.palette.primary.hue)};
    outline-offset: 3px;
  }

  @media ${theme.device.tablet} {
    left: 12px;
    bottom: 12px;
    padding: 9px 12px;
    font-size: 12px;
  }
`;

/**
 * Admin-only floating control that opens the same path on the opposite
 * HELM environment (staging ↔ production). Visibility is gated by
 * permission (`env.toggle`) + the showEnvHopButton user setting. Anchored
 * lower-left so it does not collide with Firebase Status / other lower-right chrome.
 */
const EnvironmentHopButton: React.FC = () => {
  const { hasPermission } = usePermissions();
  const userSettings = useRecoilValue(userSettingsAtom);
  const staging = useTestDataRemote;

  const canHop = hasPermission('env.toggle');
  const showButton = userSettings?.boolean?.showEnvHopButton
    ?? USER_SETTINGS_DEFAULTS.boolean.showEnvHopButton;

  if (!canHop || !showButton) return null;

  const label = getAlternateEnvironmentLabel();

  const onClick = () => {
    window.location.assign(getAlternateEnvironmentUrl());
  };

  return (
    <HopButton
      type="button"
      $staging={staging}
      onClick={onClick}
      title={label}
      aria-label={label}
    >
      <SwapOutlined />
      {label}
    </HopButton>
  );
};

export default EnvironmentHopButton;
